import { cn } from "#utils";

export interface SkeletonProps {
  /** The shape/variant of the skeleton */
  variant?: "text" | "rectangular" | "circular";
  /** Width of the skeleton. Can be a number (px) or string with units */
  width?: number | string;
  /** Height of the skeleton. Can be a number (px) or string with units */
  height?: number | string;
  /** Additional CSS classes */
  className?: string;
  /** Optional aria-label for accessibility */
  "aria-label"?: string;
}

export function Skeleton({
  variant = "text",
  width,
  height,
  className,
  "aria-label": ariaLabel = "Loading…",
}: SkeletonProps) {
  // Convert numeric values to px strings
  const widthStyle = width ? (typeof width === "number" ? `${width}px` : width) : undefined;
  const heightStyle = height ? (typeof height === "number" ? `${height}px` : height) : undefined;

  // Default heights based on variant
  const defaultHeight = variant === "text" ? "1em" : undefined;

  // Variant-specific styles
  const variantClasses = {
    text: "rounded-1",
    rectangular: "rounded-1",
    circular: "rounded-full",
  };

  return (
    <div
      aria-busy="true"
      aria-label={ariaLabel}
      aria-live="polite"
      className={cn(
        // Base styles
        "relative overflow-hidden bg-background-secondary",
        // Animation - shimmer effect
        "before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer",
        "before:bg-gradient-to-r before:from-transparent before:via-background-tertiary before:to-transparent",
        // Variant-specific border radius
        variantClasses[variant],
        className,
      )}
      style={{
        width: widthStyle,
        height: heightStyle ?? defaultHeight,
      }}
    />
  );
}
